import { useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, Clock, History, RotateCcw, Download, Maximize2, X, Loader2, Clapperboard } from 'lucide-react';
import { CaptureVoiceModal } from './NativeVoice';

export function VideoResult({ projectId, sceneId, scene, fallbackPath, onUpdated, characters = [], onCharacterUpdated }) {
  const [busy, setBusy] = useState(false);
  const [capture, setCapture] = useState(null);
  const [downloadingIdx, setDownloadingIdx] = useState(null);
  const [preview, setPreview] = useState(null);

  const currentPath = scene?.video_path || fallbackPath;
  if (!currentPath) return null;
  const approved = !!scene?.video_approved;
  const history = scene?.video_history || [];
  const previous = [...history].reverse().filter((p, i, arr) => p !== currentPath && arr.indexOf(p) === i);

  const toggleApprove = async () => {
    setBusy(true);
    try {
      const res = await api.put(`/projects/${projectId}/scenes/${sceneId}`, { video_approved: !approved });
      onUpdated?.(res.data);
      toast.success(!approved ? 'Video approved' : 'Moved back to pending');
    } catch { toast.error('Failed to update approval'); }
    finally { setBusy(false); }
  };

  const restore = async (path) => {
    setBusy(true);
    try {
      const res = await api.post(`/projects/${projectId}/scenes/${sceneId}/restore-video`, { path });
      onUpdated?.(res.data);
      toast.success('Restored this take');
    } catch { toast.error('Failed to restore'); }
    finally { setBusy(false); }
  };

  const download = async (path, idx) => {
    setDownloadingIdx(idx);
    try {
      const res = await api.get(`/files/${path}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(scene?.title || 'scene').replace(/[^a-zA-Z0-9]+/g, '_')}-take-${idx}.mp4`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
    finally { setDownloadingIdx(null); }
  };

  return (
    <div className="mb-4">
      <div className="rounded-lg overflow-hidden bg-black" data-testid="video-player">
        <video controls className="w-full max-h-[400px]" src={getImageUrl(currentPath)} />
      </div>

      {/* Approval toggle */}
      <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
        <button
          data-testid="video-approve-toggle"
          onClick={toggleApprove}
          disabled={busy}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all disabled:opacity-50 ${approved
            ? 'bg-[#22C55E]/15 text-[#22C55E] border-[#22C55E]/40'
            : 'bg-[#F59E0B]/12 text-[#F59E0B] border-[#F59E0B]/30'}`}
          title={approved ? 'Click to move back to Pending' : 'Click to mark Approved'}
        >
          {approved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
          <span data-testid="video-status-label">{approved ? 'Approved' : 'Pending'}</span>
        </button>
        <div className="flex items-center gap-3">
          {characters.length > 0 && (scene?.character_ids || []).length > 0 && (
            <button data-testid="capture-voice-btn" onClick={() => setCapture(currentPath)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-[#E879F9]/30 bg-[#E879F9]/10 text-[#E879F9] hover:bg-[#E879F9]/20"
              title="Clone a character's voice from this take and lock it as their Kling native voice">
              <Clapperboard className="w-3.5 h-3.5" /> Capture voice
            </button>
          )}
          {previous.length > 0 && (
            <span className="text-[11px] text-[#8A8A93] flex items-center gap-1"><History className="w-3 h-3" /> {previous.length} previous take{previous.length > 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* Previous versions */}
      {previous.length > 0 && (
        <div className="mt-3" data-testid="previous-versions">
          <p className="text-[10px] tracking-[0.15em] uppercase font-semibold text-[#8A8A93] mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Previous versions</p>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {previous.map((path, i) => {
              const takeNo = previous.length - i;
              return (
                <div key={path} data-testid={`prev-video-${i}`} className="shrink-0 w-40 rounded-lg border border-white/[0.06] bg-[#141418] overflow-hidden">
                  <div className="relative aspect-video bg-black group">
                    <video className="w-full h-full object-cover" src={getImageUrl(path)} muted preload="metadata" />
                    <button data-testid={`prev-preview-${i}`} onClick={() => setPreview({ path, takeNo })}
                      className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="flex items-center gap-1 text-[10px] font-medium text-white bg-black/60 px-2 py-1 rounded"><Maximize2 className="w-3 h-3" /> Preview</span>
                    </button>
                  </div>
                  <div className="p-2">
                    <p className="text-[10px] text-[#8A8A93] mb-1.5">Take {takeNo}</p>
                    <div className="flex gap-1.5">
                      <button data-testid={`prev-restore-${i}`} onClick={() => restore(path)} disabled={busy}
                        className="flex-1 flex items-center justify-center gap-1 text-[10px] py-1.5 rounded bg-[#E879F9]/15 text-[#E879F9] hover:bg-[#E879F9]/25 border border-[#E879F9]/30 disabled:opacity-40">
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                      <button data-testid={`prev-download-${i}`} onClick={() => download(path, takeNo)} disabled={downloadingIdx === takeNo}
                        className="flex items-center justify-center gap-1 text-[10px] py-1.5 px-2 rounded bg-white/[0.05] text-[#8A8A93] hover:text-white border border-white/10 disabled:opacity-40">
                        {downloadingIdx === takeNo ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 p-4" data-testid="prev-preview-modal" onClick={() => setPreview(null)}>
          <div className="max-w-3xl w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-white">Take {preview.takeNo}</span>
              <button onClick={() => setPreview(null)} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]"><X className="w-5 h-5" /></button>
            </div>
            <div className="rounded-lg overflow-hidden bg-black"><video controls autoPlay className="w-full max-h-[70vh]" src={getImageUrl(preview.path)} /></div>
            <div className="flex justify-end gap-2 mt-3">
              {characters.length > 0 && (
                <button data-testid="prev-modal-capture" onClick={() => { setCapture(preview.path); setPreview(null); }}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/[0.06] text-[#E879F9] hover:text-white border border-[#E879F9]/30"><Clapperboard className="w-3.5 h-3.5" /> Capture voice</button>
              )}
              <button data-testid="prev-modal-download" onClick={() => download(preview.path, preview.takeNo)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-white/[0.06] text-[#8A8A93] hover:text-white border border-white/10"><Download className="w-3.5 h-3.5" /> Download</button>
              <button data-testid="prev-modal-restore" onClick={() => { restore(preview.path); setPreview(null); }}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-[#E879F9]/15 text-[#E879F9] hover:bg-[#E879F9]/25 border border-[#E879F9]/30"><RotateCcw className="w-3.5 h-3.5" /> Restore this take</button>
            </div>
          </div>
        </div>
      )}

      {capture && (
        <CaptureVoiceModal projectId={projectId} sceneId={sceneId} scene={scene} videoPath={capture} characters={characters}
          onClose={() => setCapture(null)} onCaptured={(c) => onCharacterUpdated?.(c)} />
      )}
    </div>
  );
}
